import React, { useEffect, useMemo, useState } from "react";

/* ------------------------------------------------------------------ */
/*  ConfettiSplash — reusable victory animation for any game          */
/*  Props:                                                            */
/*    active      – show confetti when true (e.g. game.winner !== 0)  */
/*    winnerName  – optional victory message                          */
/*    pieceCount  – number of confetti pieces (default 120)           */
/*    durationMs  – how long the animation runs (default 8000)        */
/* ------------------------------------------------------------------ */

interface ConfettiSplashProps {
  active: boolean;
  winnerName?: string;
  pieceCount?: number;
  durationMs?: number;
}

interface Piece {
  id: number;
  left: number;
  delay: number;
  color: string;
  size: number;
  shape: "square" | "circle" | "triangle";
  rotStart: number;
  rotEnd: number;
  drift: number;
}

const PALETTE = [
  "#ff6b6b", "#ffd93d", "#6bcb77", "#4d96ff",
  "#ff8c42", "#a66cff", "#ff6eb4", "#00d2ff",
  "#f9ca24", "#eb4d4b", "#7ed6df", "#e056a0",
];

function randomPiece(id: number): Piece {
  const shape = (["square", "circle", "triangle"] as const)[
    Math.floor(Math.random() * 3)
  ];
  return {
    id,
    left: Math.random() * 100,
    delay: Math.random() * 1.5,
    color: PALETTE[Math.floor(Math.random() * PALETTE.length)],
    size: 6 + Math.random() * 8,
    shape,
    rotStart: Math.random() * 360,
    rotEnd: 180 + Math.random() * 540,
    drift: -40 + Math.random() * 80,
  };
}

export default function ConfettiSplash({
  active,
  winnerName,
  pieceCount = 120,
  durationMs = 8000,
}: ConfettiSplashProps) {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (!active) return;
    setVisible(true);
    const t = setTimeout(() => setVisible(false), durationMs);
    return () => clearTimeout(t);
  }, [active, durationMs]);

  const pieces = useMemo(
    () => Array.from({ length: pieceCount }, (_, i) => randomPiece(i)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [active]
  );

  if (!visible) return null;

  return (
    <div className="confetti-overlay" aria-hidden="true">
      {winnerName && (
        <div className="confetti-banner">
          <span className="confetti-trophy">🏆</span>
          <span className="confetti-winner">{winnerName} wins!</span>
          <span className="confetti-trophy">🏆</span>
        </div>
      )}

      {pieces.map((p) => {
        const style: React.CSSProperties = {
          left: `${p.left}%`,
          animationDelay: `${p.delay}s`,
          animationDuration: `${2 + Math.random() * 2}s`,
          backgroundColor: p.shape !== "triangle" ? p.color : "transparent",
          width: p.size,
          height: p.size,
          borderRadius: p.shape === "circle" ? "50%" : p.shape === "square" ? "2px" : 0,
          ["--drift" as string]: `${p.drift}px`,
          ["--rot-start" as string]: `${p.rotStart}deg`,
          ["--rot-end" as string]: `${p.rotEnd}deg`,
        };
        return <div key={p.id} className="confetti-piece" style={style} />;
      })}
    </div>
  );
}
